
import { supabase } from '@/integrations/supabase/client';
import { Event, EventOption, Guest } from '@/types/event';
import { verifyEventAccess, verifyGuestAccess, sanitizeInput, logSecurityEvent } from '@/lib/security';
import { eventSchema, guestSchema } from '@/lib/validation';
import { validateDatabaseAuth, withAuthRetry } from '@/lib/authUtils';

export interface CreateEventData {
  name: string;
  description: string;
  hostName: string;
  deadline?: string;
  eventType: 'voting' | 'rsvp';
  options: Array<{
    type: 'date' | 'time' | 'location';
    value: string;
  }>;
  guests: Array<{
    email?: string;
    phone?: string;
    name?: string;
  }>;
  rsvpDate?: string;
  rsvpTime?: string;
  rsvpLocation?: string;
  allowGuestSuggestions?: boolean;
  clientId?: string;
}

export type VoteType = 'prefer' | 'unavailable' | 'maybe' | 'accept' | 'decline';

export interface VoteData {
  guestId: string;
  votes: Array<{
    optionId: string;
    voteType: VoteType;
  }>;
}

export const eventService = {
  // Get event by ID for event owners (simple, direct access)
  async getEventById(eventId: string): Promise<Event | null> {
    try {
      console.log('Getting event by ID for owner:', eventId);
      
      // Get event data with owner check via RLS
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();

      if (eventError) {
        console.error('Error fetching event by ID:', eventError);
        return null;
      }

      if (!eventData) {
        console.log('No event found for ID:', eventId);
        return null;
      }

      console.log('Event loaded by ID:', eventData);

      // Fetch related data separately
      const [optionsData, guestsData, votesData] = await Promise.all([
        supabase
          .from('event_options')
          .select('*')
          .eq('event_id', eventData.id),
        
        supabase
          .from('event_guests')
          .select('*')
          .eq('event_id', eventData.id),
        
        supabase
          .from('event_votes')
          .select('*')
          .eq('event_id', eventData.id)
      ]);

      // Handle errors gracefully - don't fail the entire event loading if related data fails
      if (optionsData.error || guestsData.error || votesData.error) {
        console.error('Error fetching related data (proceeding with empty arrays):', {
          optionsError: optionsData.error,
          guestsError: guestsData.error,
          votesError: votesData.error
        });
        
        // Return event with empty arrays for failed data
        return this.transformEventData(
          eventData,
          optionsData.error ? [] : (optionsData.data || []),
          guestsData.error ? [] : (guestsData.data || []),
          votesData.error ? [] : (votesData.data || [])
        );
      }
      
      return this.transformEventData(
        eventData,
        optionsData.data || [],
        guestsData.data || [],
        votesData.data || []
      );
    } catch (error) {
      console.error('Error in getEventById:', error);
      return null;
    }
  },

  // Create a new event with bulletproof authentication
  async createEvent(data: CreateEventData): Promise<Event> {
    return withAuthRetry(async () => {
      console.log('🚀 Starting bulletproof event creation...');
      
      // Comprehensive database-level auth validation
      const dbAuthResult = await validateDatabaseAuth();
      if (!dbAuthResult.isValid) {
        console.error('❌ Database auth validation failed:', dbAuthResult);
        throw new Error(`Authentication failed: ${dbAuthResult.error || 'Database authentication invalid'}`);
      }
      
      console.log('✅ Database auth validated for user:', dbAuthResult.userId);
      
      // Try secure database function first
      try {
        console.log('🔧 Attempting secure database event creation...');
        
        const { data: eventId, error: dbError } = await supabase
          .rpc('create_event_with_auth_validation', {
            p_name: data.name,
            p_host_name: data.hostName,
            p_event_type: data.eventType,
            p_description: data.description,
            p_deadline: data.deadline ? new Date(data.deadline).toISOString() : null,
            p_rsvp_date: data.rsvpDate,
            p_rsvp_time: data.rsvpTime,
            p_rsvp_location: data.rsvpLocation,
            p_allow_guest_suggestions: data.allowGuestSuggestions || false
          });
          
        if (dbError) {
          console.error('❌ Secure database function failed:', dbError);
          throw new Error(`Database event creation failed: ${dbError.message}`);
        }
        
        console.log('✅ Event created securely via database function:', eventId);
        
        // Now handle options and guests
        return await this.completeEventCreation(eventId, data);
        
      } catch (dbFuncError: any) {
        console.log('⚠️ Database function failed, falling back to direct creation:', dbFuncError);
        
        // Fallback to traditional creation with extra validation
        return await this.createEventFallback(data, dbAuthResult.userId!);
      }
    });
  },

  // Complete event creation by adding options and guests
  async completeEventCreation(eventId: string, data: CreateEventData): Promise<Event> {
    // Validate and sanitize input
    const validatedData = eventSchema.parse({
      name: sanitizeInput(data.name),
      description: data.description ? sanitizeInput(data.description) : undefined,
      hostName: sanitizeInput(data.hostName),
      deadline: data.deadline ? new Date(data.deadline) : undefined,
      eventType: data.eventType,
      rsvpDate: data.rsvpDate,
      rsvpTime: data.rsvpTime,
      rsvpLocation: data.rsvpLocation
    });

    // Validate guests
    const validatedGuests = data.guests.map(guest => 
      guestSchema.parse({
        name: guest.name ? sanitizeInput(guest.name) : undefined,
        email: guest.email,
        phone: guest.phone
      })
    );
    
    try {
      // Get the created event
      const { data: event, error: eventError } = await supabase
        .from('events')
        .select()
        .eq('id', eventId)
        .single();

      if (eventError) {
        console.error('Event retrieval error:', eventError);
        throw new Error(`Failed to retrieve created event: ${eventError.message}`);
      }

      // Update client_id if provided (RPC function doesn't handle it)
      if (data.clientId) {
        const { error: updateError } = await supabase
          .from('events')
          .update({ client_id: data.clientId })
          .eq('id', eventId);
        
        if (updateError) {
          console.error('Failed to assign client:', updateError);
        }
      }

      // Create event options only for voting events
      let options = [];
      
      if (data.eventType === 'voting') {
        const optionsData = data.options.map(option => ({
          event_id: event.id,
          type: option.type,
          value: option.value,
        }));

        const { data: createdOptions, error: optionsError } = await supabase
          .from('event_options')
          .insert(optionsData)
          .select();

        if (optionsError) {
          console.error('Options creation error:', optionsError);
          // Clean up the event if options fail
          await supabase.from('events').delete().eq('id', event.id);
          throw new Error(`Failed to create event options: ${optionsError.message}`);
        }

        options = createdOptions;
      }
      // For RSVP events, we don't create any options in the event_options table
      // The RSVP responses will be handled differently

      // Create event guests
      const guestsData = validatedGuests.map(guest => ({
        event_id: event.id,
        email: guest.email,
        phone: guest.phone,
        name: guest.name,
      }));

      const { data: guests, error: guestsError } = await supabase
        .from('event_guests')
        .insert(guestsData)
        .select();

      if (guestsError) {
        console.error('Guests creation error:', guestsError);
        // Clean up event and options if guests fail
        if (data.eventType === 'voting') {
          await supabase.from('event_options').delete().eq('event_id', event.id);
        }
        await supabase.from('events').delete().eq('id', event.id);
        throw new Error(`Failed to invite guests: ${guestsError.message}`);
      }

      // Log security event - get current user for logging
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (user) {
        await logSecurityEvent('event_created', { 
          event_id: event.id, 
          event_name: event.name,
          guest_count: guests.length 
        }, user.id);
      }

      return this.transformEventData(event, options, guests, []);
    } catch (error: any) {
      console.error('Event completion failed:', error);
      
      // Clean up event if adding options/guests failed
      if (eventId) {
        try {
          await supabase.from('events').delete().eq('id', eventId);
          console.log('Cleaned up failed event:', eventId);
        } catch (cleanupError) {
          console.error('Failed to cleanup event:', cleanupError);
        }
      }
      
      throw new Error(`Failed to complete event creation: ${error.message || 'Unknown error occurred'}`);
    }
  },

  // Fallback event creation method with traditional approach
  async createEventFallback(data: CreateEventData, userId: string): Promise<Event> {
    console.log('📦 Using fallback event creation for user:', userId);
    
    // Get current user for profile check
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated for fallback creation');
    }

    // Ensure user profile exists (fail-safe mechanism)
    await this.ensureUserProfile(user);

    // Validate and sanitize input
    const validatedData = eventSchema.parse({
      name: sanitizeInput(data.name),
      description: data.description ? sanitizeInput(data.description) : undefined,
      hostName: sanitizeInput(data.hostName),
      deadline: data.deadline ? new Date(data.deadline) : undefined,
      eventType: data.eventType,
      rsvpDate: data.rsvpDate,
      rsvpTime: data.rsvpTime,
      rsvpLocation: data.rsvpLocation
    });

    // Validate guests
    const validatedGuests = data.guests.map(guest => 
      guestSchema.parse({
        name: guest.name ? sanitizeInput(guest.name) : undefined,
        email: guest.email,
        phone: guest.phone
      })
    );

      // Use a transaction-like approach by creating all data together
      try {
        // Create the event
        const { data: event, error: eventError } = await supabase
          .from('events')
          .insert({
            name: validatedData.name,
            description: validatedData.description,
            host_name: validatedData.hostName,
            deadline: validatedData.deadline ? validatedData.deadline.toISOString() : null,
            user_id: userId,
            event_type: data.eventType,
            rsvp_date: data.rsvpDate,
            rsvp_time: data.rsvpTime,
            rsvp_location: data.rsvpLocation,
            allow_guest_suggestions: data.allowGuestSuggestions || false,
            client_id: data.clientId || null,
          })
          .select()
          .single();

      if (eventError) {
        console.error('Fallback event creation error:', eventError);
        throw new Error(`Failed to create event: ${eventError.message}`);
      }

      console.log('✅ Fallback event created:', event.id);
      
      // Complete with options and guests
      return await this.completeEventCreation(event.id, data);
      
    } catch (error: any) {
      console.error('Fallback event creation failed:', error);
      
      // Provide more specific error messages
      if (error.message?.includes('profile') || error.message?.includes('user_id')) {
        throw new Error('Unable to create event. Please refresh the page and try again. If the problem persists, contact support.');
      }
      
      if (error.message?.includes('auth')) {
        throw new Error('Authentication error. Please log out and log back in.');
      }
      
      if (error.message?.includes('subscription') || error.message?.includes('limit')) {
        throw new Error(error.message);
      }
      
      throw new Error(`Failed to create event: ${error.message || 'Unknown error occurred'}`);
    }
  },

  // Ensure user profile exists (fail-safe mechanism)
  async ensureUserProfile(user: any): Promise<void> {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error checking profile:', error);
        throw new Error('Unable to verify user profile. Please try again.');
      }

      if (!profile) {
        console.log('Profile not found, creating one for user:', user.id);
        
        const displayName = user.user_metadata?.display_name || user.email?.split('@')[0] || 'User';
        
        const { error: createError } = await supabase
          .from('profiles')
          .insert({
            user_id: user.id,
            display_name: displayName
          });

        if (createError) {
          console.error('Error creating profile:', createError);
          throw new Error('Unable to create user profile. Please contact support.');
        }
        
        console.log('Profile created successfully for user:', user.id);
      }
    } catch (error: any) {
      console.error('Profile verification failed:', error);
      throw error;
    }
  },

  // Get events for the current user (created by user OR invited as guest) - Fixed approach
  async getUserEvents(): Promise<Event[]> {
    console.log('🚀 [getUserEvents] Starting fixed getUserEvents...');
    
    // Get current user first
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      console.error('❌ [getUserEvents] Auth error:', authError);
      throw new Error('User not authenticated. Please log in and try again.');
    }

    console.log('✅ [getUserEvents] Authenticated user:', {
      id: user.id,
      email: user.email,
      aud: user.aud,
      role: user.role
    });

    try {
      // Step 1: Get owned events (simple query without joins)
      console.log('🔍 [getUserEvents] Fetching owned events...');
      const { data: ownedEvents, error: ownedError } = await supabase
        .from('events')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (ownedError) {
        console.error('❌ [getUserEvents] Error fetching owned events:', ownedError);
        throw ownedError;
      }

      // Step 2: Get invited event IDs
      console.log('🔍 [getUserEvents] Fetching invited event IDs for email:', user.email);
      const { data: invitedEventIds, error: invitedIdsError } = await supabase
        .from('event_guests')
        .select('event_id')
        .eq('email', user.email);

      if (invitedIdsError) {
        console.error('❌ [getUserEvents] Error fetching invited event IDs:', invitedIdsError);
        console.error('❌ [getUserEvents] Full error details:', JSON.stringify(invitedIdsError, null, 2));
      } else {
        console.log('✅ [getUserEvents] Successfully fetched invited event IDs without error');
      }

      console.log('🔍 [getUserEvents] Invited event IDs found:', invitedEventIds?.length || 0, 
        invitedEventIds?.map(row => row.event_id) || []);

      let invitedEvents: any[] = [];
      if (invitedEventIds && invitedEventIds.length > 0) {
        const eventIds = invitedEventIds.map(row => row.event_id);
        console.log('🔍 [getUserEvents] Fetching invited events details...');
        
        const { data: fetchedInvitedEvents, error: invitedError } = await supabase
          .from('events')
          .select('*')
          .in('id', eventIds)
          .eq('is_active', true)
          .neq('user_id', user.id) // Exclude events user owns
          .order('created_at', { ascending: false });

        if (invitedError) {
          console.error('❌ [getUserEvents] Error fetching invited events:', invitedError);
        } else {
          invitedEvents = fetchedInvitedEvents || [];
        }
      }

      // Combine events
      const allEvents = [...(ownedEvents || []), ...invitedEvents];
      const uniqueEvents = allEvents.filter((event, index, self) => 
        index === self.findIndex(e => e.id === event.id)
      );

      console.log('✅ [getUserEvents] Events fetched - DETAILED DEBUG:', {
        ownedCount: ownedEvents?.length || 0,
        invitedCount: invitedEvents.length,
        totalUnique: uniqueEvents.length,
        invitedEventIds: invitedEventIds?.map(row => row.event_id) || [],
        fetchedInvitedEvents: invitedEvents.map(e => ({ id: e.id, name: e.name, user_id: e.user_id })),
        ownedEventIds: ownedEvents?.map(e => e.id) || [],
        userEmail: user.email,
        timestamp: Date.now(),
        debugInfo: 'SHOULD SHOW INVITED EVENTS - ' + Date.now()
      });

      if (uniqueEvents.length === 0) {
        console.log('📊 [getUserEvents] No events found, returning empty array');
        return [];
      }

      // Step 3: Fetch related data for all events
      const eventIds = uniqueEvents.map(e => e.id);
      
      console.log('🔍 [getUserEvents] Fetching related data...');
      const [optionsResult, guestsResult, votesResult] = await Promise.all([
        supabase.from('event_options').select('*').in('event_id', eventIds),
        supabase.from('event_guests').select('*').in('event_id', eventIds),
        supabase.from('event_votes').select('*').in('event_id', eventIds)
      ]);

      const options = optionsResult.data || [];
      const guests = guestsResult.data || [];
      const votes = votesResult.data || [];

      console.log('✅ [getUserEvents] Related data fetched:', {
        optionsCount: options.length,
        guestsCount: guests.length,
        votesCount: votes.length
      });

      // Transform events
      const transformedEvents = uniqueEvents.map(event => {
        const eventOptions = options.filter(o => o.event_id === event.id);
        const eventGuests = guests.filter(g => g.event_id === event.id);
        const eventVotes = votes.filter(v => v.event_id === event.id);
        
        return this.transformEventData(event, eventOptions, eventGuests, eventVotes);
      });

      console.log('✅ [getUserEvents] Events transformed successfully:', {
        count: transformedEvents.length,
        events: transformedEvents.map(e => ({
          id: e.id,
          name: e.name,
          guestCount: e.guests?.length || 0,
          isOwned: e.userId === user.id
        }))
      });

      return transformedEvents;

    } catch (error) {
      console.error('💥 [getUserEvents] Unexpected error:', error);
      throw error;
    }
  },

  // Get a single event by ID (delegating to getEventById)
  async getEvent(eventId: string): Promise<Event | null> {
    return this.getEventById(eventId);
  },

  // Get event by shareable link (for authenticated users)
  async getEventByShareableLink(shareableLink: string): Promise<Event | null> {
    try {
      console.log('Getting event by shareable link:', shareableLink);
      
      // First, get the event data
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('shareable_link', shareableLink)
        .eq('is_active', true)
        .maybeSingle();

      if (eventError) {
        console.error('Error fetching event by shareable link:', eventError);
        return null;
      }

      if (!eventData) {
        console.log('No event found for shareable link:', shareableLink);
        return null;
      }

      console.log('Event loaded:', eventData);

      // Now fetch related data separately to avoid ambiguous column references
      const [optionsData, guestsData, votesData] = await Promise.all([
        // Event options
        supabase
          .from('event_options')
          .select('*')
          .eq('event_id', eventData.id),
        
        // Event guests
        supabase
          .from('event_guests')
          .select('*')
          .eq('event_id', eventData.id),
        
        // Event votes
        supabase
          .from('event_votes')
          .select('*')
          .eq('event_id', eventData.id)
      ]);

      // Handle errors gracefully - don't fail the entire event loading if related data fails
      if (optionsData.error || guestsData.error || votesData.error) {
        console.error('Error fetching related data (proceeding with empty arrays):', {
          optionsError: optionsData.error,
          guestsError: guestsData.error,
          votesError: votesData.error
        });
        
        // Return event with empty arrays for failed data
        return this.transformEventData(
          eventData,
          optionsData.error ? [] : (optionsData.data || []),
          guestsData.error ? [] : (guestsData.data || []),
          votesData.error ? [] : (votesData.data || [])
        );
      }
      
      return this.transformEventData(
        eventData,
        optionsData.data || [],
        guestsData.data || [],
        votesData.data || []
      );
    } catch (error) {
      console.error('Error in getEventByShareableLink:', error);
      return null;
    }
  },

  // Find or create guest for voting (anonymous access)
  async findOrCreateGuest(eventId: string, guestInfo: { name?: string; email?: string; phone?: string }): Promise<Guest | null> {
    // First try to find existing guest
    let query = supabase
      .from('event_guests')
      .select('*')
      .eq('event_id', eventId);

    if (guestInfo.email) {
      query = query.eq('email', guestInfo.email);
    } else if (guestInfo.phone) {
      query = query.eq('phone', guestInfo.phone);
    } else if (guestInfo.name) {
      query = query.eq('name', guestInfo.name);
    } else {
      return null;
    }

    const { data: existingGuests } = await query;
    
    if (existingGuests && existingGuests.length > 0) {
      const guest = existingGuests[0];
      return {
        id: guest.id,
        email: guest.email,
        phone: guest.phone,
        name: guest.name,
        hasVoted: guest.has_voted,
        invitedAt: guest.invited_at,
      };
    }

    return null; // Guest not found in invite list
  },

  // Submit votes for an event (supports both voting and RSVP)
  async submitVotes(eventId: string, guestId: string, votes: Array<{ optionId: string; voteType: VoteType }>): Promise<void> {
    // Rate limiting for voting is now handled by the server-side edge function

    // Verify guest access
    const hasGuestAccess = await verifyGuestAccess(guestId, eventId);
    if (!hasGuestAccess) {
      await logSecurityEvent('unauthorized_vote_attempt', { event_id: eventId, guest_id: guestId });
      throw new Error('Unauthorized: Invalid guest access for this event');
    }
    console.log('Starting submitVotes for guest:', guestId, 'event:', eventId);
    
    // Get event to check type
    const { data: event, error: eventError } = await supabase
      .from('events')
      .select('event_type')
      .eq('id', eventId)
      .single();

    if (eventError) {
      console.error('Error fetching event:', eventError);
      throw eventError;
    }

    console.log('Event type:', event.event_type);

    // Delete existing votes for this guest
    const { error: deleteError } = await supabase
      .from('event_votes')
      .delete()
      .eq('guest_id', guestId);

    if (deleteError) {
      console.error('Error deleting existing votes:', deleteError);
      throw deleteError;
    }

    console.log('Deleted existing votes for guest:', guestId);

    // For RSVP events, we need to handle differently since there are no actual options
    if (event.event_type === 'rsvp') {
      // For RSVP events, we'll create a special vote record without an option_id
      // We'll use a NULL option_id to indicate this is an RSVP response
      const rsvpVote = votes[0]; // Should only be one vote for RSVP
      
      const { error } = await supabase
        .from('event_votes')
        .insert({
          event_id: eventId,
          guest_id: guestId,
          option_id: null, // No option for RSVP responses
          vote_type: rsvpVote.voteType,
        });

      if (error) {
        console.error('Error inserting RSVP vote:', error);
        throw error;
      }
      
      console.log('Successfully inserted RSVP vote:', rsvpVote.voteType);
    } else {
      // For voting events, use the original logic
      const votesData = votes.map(vote => ({
        event_id: eventId,
        guest_id: guestId,
        option_id: vote.optionId,
        vote_type: vote.voteType,
      }));

      const { error } = await supabase
        .from('event_votes')
        .insert(votesData);

      if (error) {
        console.error('Error inserting votes:', error);
        throw error;
      }
      
      console.log('Successfully inserted votes:', votesData);
    }

    // Update guest has_voted status - this needs to be done by the event owner
    // Let's check if the current user is the event owner
    const { data: currentEvent, error: currentEventError } = await supabase
      .from('events')
      .select('user_id')
      .eq('id', eventId)
      .single();

    if (currentEventError) {
      console.error('Error fetching current event:', currentEventError);
      throw currentEventError;
    }

    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      console.error('Error getting current user:', userError);
      throw userError || new Error('No user found');
    }

    console.log('Current user:', user.id, 'Event owner:', currentEvent.user_id);

    // Update has_voted flag
    const { error: updateError } = await supabase
      .from('event_guests')
      .update({ has_voted: true })
      .eq('id', guestId);
    
    if (updateError) {
      console.error('Error updating guest has_voted status:', updateError);
      console.error('Guest ID:', guestId);
      console.error('Current user ID:', user.id);
      console.error('Event owner ID:', currentEvent.user_id);
      
      // Don't throw the error, just log it - the vote was recorded successfully
      console.log('Vote was recorded successfully, but has_voted flag update failed');
    } else {
      console.log('Successfully updated guest has_voted status for guest:', guestId);
    }

    // Send vote notification email if voting was successful
    try {
      await this.sendVoteNotification(eventId, guestId, votes);
    } catch (notificationError) {
      // Don't fail the vote if notification fails
      console.error('Failed to send vote notification:', notificationError);
    }
  },

  // Send vote notification to host
  async sendVoteNotification(eventId: string, guestId: string, votes: Array<{ optionId: string; voteType: VoteType }>): Promise<void> {
    try {
      // Get guest information
      const { data: guest, error: guestError } = await supabase
        .from('event_guests')
        .select('name, email')
        .eq('id', guestId)
        .single();

      if (guestError || !guest) {
        console.error('Error fetching guest for notification:', guestError);
        return;
      }

      // Get event and option details for vote summary
      const { data: event, error: eventError } = await supabase
        .from('events')
        .select('id, name, event_type')
        .eq('id', eventId)
        .single();

      if (eventError || !event) {
        console.error('Error fetching event for notification:', eventError);
        return;
      }

      let voteSummary = {
        preferred_options: [] as string[],
        unavailable_options: [] as string[]
      };

      // For voting events, get option details
      if (event.event_type === 'voting') {
        const optionIds = votes.map(v => v.optionId).filter(Boolean);
        
        if (optionIds.length > 0) {
          const { data: options, error: optionsError } = await supabase
            .from('event_options')
            .select('id, type, value')
            .in('id', optionIds);

          if (!optionsError && options) {
            votes.forEach(vote => {
              const option = options.find(o => o.id === vote.optionId);
              if (option) {
                const optionLabel = `${option.type}: ${option.value}`;
                if (vote.voteType === 'prefer') {
                  voteSummary.preferred_options.push(optionLabel);
                } else if (vote.voteType === 'unavailable') {
                  voteSummary.unavailable_options.push(optionLabel);
                }
              }
            });
          }
        }
      } else {
        // For RSVP events, show the response
        const rsvpVote = votes[0];
        if (rsvpVote) {
          if (rsvpVote.voteType === 'accept') {
            voteSummary.preferred_options.push('Accepted the invitation');
          } else if (rsvpVote.voteType === 'decline') {
            voteSummary.unavailable_options.push('Declined the invitation');
          }
        }
      }

      // Call the vote notification edge function
      const { error: notificationError } = await supabase.functions.invoke('send-vote-notification', {
        body: {
          event_id: eventId,
          guest_name: guest.name || guest.email || 'Anonymous Guest',
          guest_email: guest.email,
          vote_summary: voteSummary
        }
      });

      if (notificationError) {
        console.error('Error calling vote notification function:', notificationError);
      } else {
        console.log('Vote notification sent successfully');
      }
    } catch (error) {
      console.error('Error in sendVoteNotification:', error);
    }
  },

  // Update event
  async updateEvent(eventId: string, updates: Partial<CreateEventData>): Promise<Event> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify event access
    const hasAccess = await verifyEventAccess(eventId, user.id);
    if (!hasAccess) {
      await logSecurityEvent('unauthorized_event_update_attempt', { event_id: eventId }, user.id);
      throw new Error('Unauthorized: You do not have permission to update this event');
    }

    // Validate and sanitize updates
    const sanitizedUpdates: any = {};
    if (updates.name) sanitizedUpdates.name = sanitizeInput(updates.name);
    if (updates.description) sanitizedUpdates.description = sanitizeInput(updates.description);
    if (updates.hostName) sanitizedUpdates.host_name = sanitizeInput(updates.hostName);
    if (updates.deadline) sanitizedUpdates.deadline = updates.deadline;

    const { data: event, error } = await supabase
      .from('events')
      .update(sanitizedUpdates)
      .eq('id', eventId)
      .select()
      .single();

    if (error) throw error;

    // Log security event
    await logSecurityEvent('event_updated', { event_id: eventId }, user.id);

    // Get updated event with relations
    return this.getEvent(eventId) as Promise<Event>;
  },

  // Delete event
  async deleteEvent(eventId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify event access
    const hasAccess = await verifyEventAccess(eventId, user.id);
    if (!hasAccess) {
      await logSecurityEvent('unauthorized_event_delete_attempt', { event_id: eventId }, user.id);
      throw new Error('Unauthorized: You do not have permission to delete this event');
    }

    const { error } = await supabase
      .from('events')
      .delete()
      .eq('id', eventId);

    if (error) throw error;

    // Log security event
    await logSecurityEvent('event_deleted', { event_id: eventId }, user.id);
  },

  // Guest suggestion methods
  async suggestOption(eventId: string, guestId: string, suggestion: { type: 'date' | 'time' | 'location'; value: string }): Promise<void> {
    const { error } = await supabase
      .from('event_options')
      .insert({
        event_id: eventId,
        type: suggestion.type,
        value: suggestion.value,
        suggested_by_guest_id: guestId,
        status: 'pending',
      });

    if (error) {
      console.error('Error suggesting option:', error);
      throw new Error('Failed to suggest option');
    }
  },

  async approveSuggestion(optionId: string, approved: boolean): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    
    const { error } = await supabase
      .from('event_options')
      .update({ status: approved ? 'approved' : 'rejected' })
      .eq('id', optionId);

    if (error) {
      console.error('Error updating suggestion status:', error);
      throw new Error('Failed to update suggestion status');
    }
  },

  // Get event guests
  async getEventGuests(eventId: string): Promise<Guest[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify event access
    const hasAccess = await verifyEventAccess(eventId, user.id);
    if (!hasAccess) {
      throw new Error('Unauthorized: You do not have permission to view guests for this event');
    }

    // Get guests and their votes to determine accurate hasVoted status
    const { data: guests, error: guestsError } = await supabase
      .from('event_guests')
      .select('*')
      .eq('event_id', eventId)
      .order('invited_at', { ascending: true });

    if (guestsError) throw guestsError;

    // Get all votes for this event
    const { data: votes, error: votesError } = await supabase
      .from('event_votes')
      .select('guest_id')
      .eq('event_id', eventId);

    if (votesError) throw votesError;

    // Transform guests to include accurate hasVoted status
    const transformedGuests: Guest[] = (guests || []).map(guest => {
      const hasVoted = (votes || []).some(vote => vote.guest_id === guest.id);
      return {
        id: guest.id,
        email: guest.email,
        phone: guest.phone,
        name: guest.name,
        hasVoted: hasVoted, // Calculate based on actual votes
        invitedAt: guest.invited_at,
      };
    });

    return transformedGuests;
  },

  // Subscribe to event changes
  subscribeToEvent(eventId: string, callback: (event: Event) => void) {
    const channel = supabase
      .channel(`event_${eventId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'events',
          filter: `id=eq.${eventId}`,
        },
        async () => {
          const event = await this.getEvent(eventId);
          if (event) callback(event);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'event_votes',
          filter: `event_id=eq.${eventId}`,
        },
        async () => {
          const event = await this.getEvent(eventId);
          if (event) callback(event);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'event_guests',
          filter: `event_id=eq.${eventId}`,
        },
        async () => {
          const event = await this.getEvent(eventId);
          if (event) callback(event);
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  },

  // Transform database data to Event interface with user ownership info
  transformEventData(event: any, options: any[], guests: any[], votes: any[]): Event {
    // For RSVP events, we create a virtual option for tracking responses
    let optionsWithVotes: EventOption[] = [];
    
    if (event.event_type === 'rsvp') {
      // For RSVP events, we look at votes with NULL option_id
      const acceptedVotes = votes.filter(v => v.vote_type === 'accept' && v.option_id === null);
      const declinedVotes = votes.filter(v => v.vote_type === 'decline' && v.option_id === null);
      const maybeVotes = votes.filter(v => v.vote_type === 'maybe' && v.option_id === null);

      const acceptedGuests = guests.filter(g => acceptedVotes.some(v => v.guest_id === g.id));
      const declinedGuests = guests.filter(g => declinedVotes.some(v => v.guest_id === g.id));
      const maybeGuests = guests.filter(g => maybeVotes.some(v => v.guest_id === g.id));

      optionsWithVotes = [{
        id: 'rsvp-responses',
        type: 'rsvp' as any,
        value: 'accept',
        votes: acceptedGuests.length,
        voters: acceptedGuests.map(g => g.id),
      }, {
        id: 'rsvp-maybe',
        type: 'rsvp' as any,
        value: 'maybe',
        votes: maybeGuests.length,
        voters: maybeGuests.map(g => g.id),
      }, {
        id: 'rsvp-decline',
        type: 'rsvp' as any,
        value: 'decline',
        votes: declinedGuests.length,
        voters: declinedGuests.map(g => g.id),
      }];
    } else {
      // For voting events, use the original logic
      optionsWithVotes = options.map(option => {
        const optionVotes = votes.filter(vote => vote.option_id === option.id && vote.vote_type === 'prefer');
        return {
          id: option.id,
          type: option.type,
          value: option.value,
          votes: optionVotes.length,
          voters: optionVotes.map(vote => vote.guest_id),
          suggestedByGuestId: option.suggested_by_guest_id,
          status: option.status || 'host',
        };
      });
    }

    // Transform guests - determine hasVoted based on actual votes
    const transformedGuests: Guest[] = guests.map(guest => {
      const hasVoted = votes.some(vote => vote.guest_id === guest.id);
      return {
        id: guest.id,
        email: guest.email,
        phone: guest.phone,
        name: guest.name,
        hasVoted: hasVoted, // Use actual vote existence rather than database flag
        invitedAt: guest.invited_at,
      };
    });

    return {
      id: event.id,
      name: event.name,
      description: event.description || '',
      hostName: event.host_name,
      userId: event.user_id,
      eventType: event.event_type || 'voting',
      options: optionsWithVotes,
      deadline: event.deadline,
      isActive: event.is_active,
      totalVoters: transformedGuests.filter(g => g.hasVoted).length,
      guests: transformedGuests,
      votes: votes.map(vote => ({
        id: vote.id,
        optionId: vote.option_id,
        guestId: vote.guest_id,
        voteType: vote.vote_type,
        eventId: vote.event_id,
        createdAt: vote.created_at
      })),
      shareableLink: event.shareable_link,
      rsvpDate: event.rsvp_date,
      rsvpTime: event.rsvp_time,
      rsvpLocation: event.rsvp_location,
      familyGroupId: event.family_group_id,
      familyGroupName: event.family_groups?.name,
      allowGuestSuggestions: event.allow_guest_suggestions,
    };
  },
};
